import { lazy, Suspense, useState } from 'react'
import { pluralize } from '@joyful/utils'

import { AgentConversationTabStatusIcon } from '../../../core/components/agent/AgentConversationTabStatusIcon'
import { Button } from '../../../core/components/Button'
import { NotificationDot } from '../../../core/components/NotificationDot'
import { DelayedSpinner } from '../../../core/components/query-state'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../core/components/Popover'
import { Tabular } from '../../../core/components/Tabular'
import { toAgentConversationTabStatus } from '../agent/agentRunStatus'
import {
  flattenAgentSessionPages,
  useInfiniteAgentSessionsQuery,
} from '../agent/AgentSessionQueries'
import type { AgentSessionConversation } from '../agent/AgentSessionQueries'
import { useIsAgentSessionUnread } from '../agent/useAgentSessionReadState'
import { AskAiNewConversationValue } from '../../hooks/useAskAiQueryState'
import { useHasUserCapability } from '../../hooks/useHasUserCapability'
import { useActiveAccount } from '../../utils/activeAccountContext'
import { useAskAi } from './AskAiContext'

const LazyAgentSessionHistoryCommand = lazy(async () => {
  const m = await import('../agent/AgentSessionHistoryCommand')
  return { default: m.AgentSessionHistoryCommand }
})

const RECENTLY_FINISHED_WINDOW_MS = 48 * 60 * 60 * 1000
const VISIBLE_REFETCH_INTERVAL_MS = 5_000
const IN_FLIGHT_REFETCH_INTERVAL_MS = 30_000
const IDLE_REFETCH_INTERVAL_MS = 120_000

export function AgentActivityNavbarButton() {
  const canAccessAgent = useHasUserCapability('agent')
  const activeAccount = useActiveAccount()

  if (!canAccessAgent || activeAccount === null) {
    return null
  }

  return <AgentActivityPopover />
}

function AgentActivityPopover() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const askAi = useAskAi()
  const isAgentSessionUnread = useIsAgentSessionUnread()
  const isChatVisible = isPopoverOpen || askAi.state.isOpen
  const sessionsQuery = useInfiniteAgentSessionsQuery({
    refetchInterval: (sessions) => {
      if (isChatVisible) {
        return VISIBLE_REFETCH_INTERVAL_MS
      }

      const { hibernating, running } = countSessions(sessions)
      return running + hibernating > 0
        ? IN_FLIGHT_REFETCH_INTERVAL_MS
        : IDLE_REFETCH_INTERVAL_MS
    },
    staleTime: 0,
  })

  const sessions = flattenAgentSessionPages(sessionsQuery.data)
  const counts = countSessions(sessions, { isUnread: isAgentSessionUnread })

  if (
    counts.running === 0 &&
    counts.hibernating === 0 &&
    counts.recentlyFinished === 0 &&
    counts.unread === 0
  ) {
    return null
  }

  const activity = getAgentActivityPresentation(counts)
  const label = getAgentActivityLabel(counts)

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={label}
          colorVariant={activity.colorVariant}
          isSelected={isPopoverOpen}
          size="sm"
          startIcon={activity.icon}
          tooltip={label}
          variant="ghost"
        >
          <Tabular>{activity.count}</Tabular>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" size="sm" padding="none">
        <Suspense fallback={<DelayedSpinner size="sm" />}>
          <LazyAgentSessionHistoryCommand
            activeConversationId={
              askAi.state.selectedConversationId ?? AskAiNewConversationValue
            }
            onSelectConversation={(conversation) => {
              setIsPopoverOpen(false)
              askAi.actions.selectConversation(conversation.id)
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Orders the navbar trigger states. Working beats hibernating so a spinner
 * only appears when at least one run is actively working. `icon` is an element
 * rather than a component so the `startIcon` slot can merge its sizing classes
 * onto the leaf it renders.
 */
function getAgentActivityPresentation({
  hibernating,
  recentlyFinished,
  running,
  unread,
}: AgentSessionCounts) {
  if (running > 0) {
    return {
      colorVariant: 'default',
      count: running,
      icon: (
        <AgentConversationTabStatusIcon
          status={toAgentConversationTabStatus('running')}
        />
      ),
    } as const
  }

  if (hibernating > 0) {
    return {
      colorVariant: 'muted',
      count: hibernating,
      icon: (
        <AgentConversationTabStatusIcon
          status={toAgentConversationTabStatus('hibernating')}
        />
      ),
    } as const
  }

  if (unread > 0) {
    return {
      colorVariant: 'default',
      count: unread,
      icon: (
        <span className="flex items-center justify-center">
          <NotificationDot colorVariant="brand" placement="inline" size="md" />
        </span>
      ),
    } as const
  }

  return {
    colorVariant: 'muted',
    count: recentlyFinished,
    icon: <AgentConversationTabStatusIcon status={null} />,
  } as const
}

interface AgentSessionCounts {
  readonly hibernating: number
  readonly recentlyFinished: number
  readonly running: number
  readonly unread: number
}

/**
 * There is no "completed" run status: a finished turn rests at `awaiting_human`
 * indefinitely, so recency is the only available signal for "just finished".
 */
function countSessions(
  sessions: ReadonlyArray<AgentSessionConversation>,
  {
    isUnread,
  }: {
    readonly isUnread?: (session: AgentSessionConversation) => boolean
  } = {},
): AgentSessionCounts {
  const oldestFinishedAt = Date.now() - RECENTLY_FINISHED_WINDOW_MS
  const counts = {
    hibernating: 0,
    recentlyFinished: 0,
    running: 0,
    unread: 0,
  }

  for (const conversation of sessions) {
    const status = conversation.latest_run_status
    if (status === 'running') {
      counts.running += 1
    } else if (status === 'hibernating') {
      counts.hibernating += 1
    } else if (
      (status === 'awaiting_human' || status === 'failed') &&
      new Date(conversation.modified_at).getTime() > oldestFinishedAt
    ) {
      counts.recentlyFinished += 1
    }

    if (isUnread?.(conversation)) {
      counts.unread += 1
    }
  }

  return counts
}

function getAgentActivityLabel({
  hibernating,
  recentlyFinished,
  running,
  unread,
}: AgentSessionCounts) {
  return [
    running > 0 ? `${pluralize(running, 'agent')} working` : null,
    hibernating > 0 ? `${pluralize(hibernating, 'agent')} waiting` : null,
    unread > 0 ? `${unread} new` : null,
    recentlyFinished > unread
      ? `${recentlyFinished} finished in the last 48 hours`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
}
